import { decompress as zstdDecompress, init as zstdInit } from '@bokuweb/zstd-wasm'
import { CONFIG, isMarketDay, MARKET_HOURS } from '../config/constants'
import { getFirebaseAuth, getOrWaitForAuthToken } from '../services/firebase'
import { API_CONFIG } from '../config/constants'
import { parseCSV, rowsToSnapshots, getCanonicalSymbol, toIST, parseScientificNotation } from './data-utils'
import { fetchNiftyTraderBatch, fetchNiftyTraderMinute, transformNiftyTraderResponse } from './niftytrader-api'
import { IndexSymbol } from '@options-oracle/types'

let isZstdInitialized = false

// Helper to Base64 Decode
function fromBase64(str: string): Uint8Array {
    return Uint8Array.from(atob(str), (c) => c.charCodeAt(0))
}

// Decompress GZIP data using DecompressionStream (Web API) - for legacy data
async function decompressGzip(compressed: Uint8Array): Promise<Uint8Array> {
    const stream = new ReadableStream({
        start(controller) {
            controller.enqueue(compressed)
            controller.close()
        },
    })

    const decompressedStream = stream.pipeThrough(new DecompressionStream('gzip'))
    const reader = decompressedStream.getReader()
    const chunks: Uint8Array[] = []

    while (true) {
        const { done, value } = await reader.read()
        if (done) break
        chunks.push(value)
    }

    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
    const combined = new Uint8Array(totalLength)
    let offset = 0
    for (const chunk of chunks) {
        combined.set(chunk, offset)
        offset += chunk.length
    }

    return combined
}

// Detect compression format from magic bytes
function detectCompression(data: Uint8Array): 'zstd' | 'gzip' | 'unknown' {
    if (data.length < 4) return 'unknown'

    // ZSTD magic: 0x28 0xB5 0x2F 0xFD
    if (data[0] === 0x28 && data[1] === 0xb5 && data[2] === 0x2f && data[3] === 0xfd) {
        return 'zstd'
    }

    // GZIP magic: 0x1F 0x8B
    if (data[0] === 0x1f && data[1] === 0x8b) {
        return 'gzip'
    }

    return 'unknown'
}

// Auto-detect and decompress data (ZSTD or GZIP)
export async function decompressBlob(compressed: Uint8Array): Promise<any> {
    const format = detectCompression(compressed)

    // Helper to log first few bytes for debugging
    const getHexHeader = (arr: Uint8Array) =>
        Array.from(arr.slice(0, 8))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join(' ')

    if (format === 'zstd') {
        // ZSTD: Initialize if needed, then decompress
        if (!isZstdInitialized) {
            await zstdInit()
            isZstdInitialized = true
        }
        const decompressed = zstdDecompress(compressed)
        const jsonStr = new TextDecoder().decode(decompressed)
        return JSON.parse(jsonStr)
    } else if (format === 'gzip') {
        // GZIP: Decompress first
        const decompressed = await decompressGzip(compressed)

        // Try JSON first (for legacy GZIP+JSON if any)
        try {
            const jsonStr = new TextDecoder().decode(decompressed)
            return JSON.parse(jsonStr)
        } catch {
            // If JSON fails, it's likely CBOR (New Worker Format)
            try {
                // Dynamic import to avoid load-time issues if not needed
                const { decode } = await import('cbor-x')
                return decode(decompressed)
            } catch (cborErr) {
                console.error('Failed to decode GZIP data via JSON or CBOR', cborErr)
                throw cborErr
            }
        }
    } else {
        // Unknown format
        // Heuristic: If it starts with 0x28, it might be ZSTD that missed detection
        if (compressed[0] === 0x28) {
            try {
                if (!isZstdInitialized) {
                    await zstdInit()
                    isZstdInitialized = true
                }
                const decompressed = zstdDecompress(compressed)
                const jsonStr = new TextDecoder().decode(decompressed)
                return JSON.parse(jsonStr)
            } catch (zstdErr) {
                // Verify if strictly ZSTD before ignoring error?
                // console.warn('Failed optimistic ZSTD decode for 0x28 start byte', zstdErr)
            }
        }

        // Try parsing as raw JSON first
        try {
            // Only try JSON if it looks like JSON (starts with '{' or '[')
            const text = new TextDecoder().decode(compressed)
            const trimmed = text.trim()
            if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                return JSON.parse(text)
            }
            throw new Error('Not JSON')
        } catch {
            // Fallback to CBOR
            try {
                const { decode } = await import('cbor-x')
                return decode(compressed)
            } catch (cborErr) {
                console.warn(
                    `Failed to decode unknown format (Header: ${getHexHeader(compressed)}) via JSON or CBOR`,
                    cborErr,
                )
                throw cborErr
            }
        }
    }
}

/**
 * Client-Side Decompression Utility
 * Offloads gzip/ZSTD decompression from Cloudflare Pages Free Tier (50ms limit) to user's browser.
 * Loops through raw compressed snapshots from D1 and unwraps them efficiently.
 */
export async function processCompressedHistory(rows: any[]): Promise<any[]> {
    const processed: any[] = []
    let lastYieldTime = performance.now();
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (performance.now() - lastYieldTime > 10) {
            await new Promise(r => setTimeout(r, 0));
            lastYieldTime = performance.now();
        }
        const dataBlob = row.data_blob || row.dataBlob;
        if (!dataBlob) {
            processed.push(row);
            continue;
        }

            try {
                const decoded = Uint8Array.from(atob(dataBlob), (c) => c.charCodeAt(0))
                
                if (
                    decoded.length >= 8 &&
                    decoded[0] === 0 &&
                    decoded[1] === 0 &&
                    decoded[2] === 0 &&
                    decoded[3] === 0
                ) {
                    continue; // Skip corrupted rows
                }

                const decompressed = await decompressBlob(decoded)

                const payload = decompressed.data ? decompressed.data : decompressed

                // Handle EOD_ARCHIVE recursive unpacking
                if (payload.version === 2 && Array.isArray(payload.ticks)) {
                    const unpackedRows = payload.ticks.map((tick: any) => ({
                        ...row,
                        timestamp: tick.t,
                        timestamp_ist: tick.t,
                        timestampIST: tick.t,
                        index_ltp: tick.ltp,
                        indexLtp: tick.ltp,
                        spotPrice: tick.ltp,
                        data_blob: tick.b,
                        source: 'D1-EOD-Archive',
                    }))
                    const subProcessed = await processCompressedHistory(unpackedRows)
                    processed.push(...subProcessed)
                    continue;
                }

                // Normalize D1 V4 Worker Format -> V2 (CSV) MarketSnapshot Format
                if (payload.strategyChainData?.strikeMap && !payload.optionsData) {
                    const rawSym = row.symbol || payload.assetKey?.split('|')[1] || payload.assetKey || 'UNKNOWN'
                    const sym = getCanonicalSymbol(rawSym)
                    const exp = row.expiry || payload.expiry || 'UNKNOWN'

                    const optArray: any[] = []
                    const strikes = Object.keys(payload.strategyChainData.strikeMap)
                    for (const strike of strikes) {
                        const sd = payload.strategyChainData.strikeMap[strike]
                        const ce = sd.callOptionData?.marketData
                        const cAn = sd.callOptionData?.analytics || {}
                        const pe = sd.putOptionData?.marketData
                        const pAn = sd.putOptionData?.analytics || {}

                        const opt: any = { strike: parseInt(strike, 10), strikePrice: parseInt(strike, 10) }
                        if (ce) {
                            opt.callPrice = ce.ltp || 0
                            opt.callVolume = ce.volume || 0
                            opt.callOpenInterest = ce.oi || 0
                            opt.callChange = ce.ltp - (ce.cp || ce.ltp)
                            opt.callChangePercent = ce.cp ? ((ce.ltp - ce.cp) / ce.cp) * 100 : ce.changePercent || 0 // added fallback
                            opt.callImpliedVolatility = cAn.iv || 0
                            opt.callDelta = cAn.delta || 0
                            opt.callTheta = cAn.theta || 0
                            opt.callGamma = cAn.gamma || 0
                            opt.callVega = cAn.vega || 0
                            opt.callOpenInterestChange = ce.oi - (ce.prevOi || ce.oi) // Will be 0 if prevOi missing
                        }
                        if (pe) {
                            opt.putPrice = pe.ltp || 0
                            opt.putVolume = pe.volume || 0
                            opt.putOpenInterest = pe.oi || 0
                            opt.putChange = pe.ltp - (pe.cp || pe.ltp)
                            opt.putChangePercent = pe.cp ? ((pe.ltp - pe.cp) / pe.cp) * 100 : pe.changePercent || 0
                            opt.putImpliedVolatility = pAn.iv || 0
                            opt.putDelta = pAn.delta || 0
                            opt.putTheta = pAn.theta || 0
                            opt.putGamma = pAn.gamma || 0
                            opt.putVega = pAn.vega || 0
                            opt.putOpenInterestChange = pe.oi - (pe.prevOi || pe.oi)
                        }
                        optArray.push(opt)
                    }

                    payload.optionsData = { [sym]: { [exp]: optArray } }

                    // Construct indexData — PRIORITY: D1 row LTP > blob LTP
                    // Handler sends 'indexLtp' (camelCase) and 'index_ltp' (snake_case)
                    const idxLtp =
                        row.index_ltp ||
                        row.indexLtp ||
                        row.spotPrice ||
                        payload.optionChainExtendedData?.assetLtpc?.ltp ||
                        payload.spotPrice ||
                        payload.indexLtp ||
                        0
                    payload.indexData = {
                        [sym]: { lastPrice: idxLtp },
                    }
                    payload.spotPrice = idxLtp
                    payload.timestampIST =
                        row.timestamp_ist || row.timestampIST || payload.timestampIST || payload.timestamp_ist || toIST(row.timestamp || payload.timestamp)
                }

                // Normalize D1 V2 Backup Format -> V2 MarketSnapshot Format
                if (payload.options && Array.isArray(payload.options) && !payload.optionsData) {
                    const rawSym = row.symbol || payload.assetKey?.split('|')[1] || payload.assetKey || payload.symbol || 'UNKNOWN'
                    const sym = getCanonicalSymbol(rawSym)
                    const exp = row.expiry || payload.expiry || 'UNKNOWN'

                    payload.optionsData = { [sym]: { [exp]: payload.options } }

                    if (!payload.indexData && payload.spotPrice) {
                        payload.indexData = { [sym]: { lastPrice: payload.spotPrice } }
                    }
                }

                let finalPayload = payload;

                // Normalize D1 V3 Base64 Raw JSON Array Format (From Google Apps Script direct sync)
                if (Array.isArray(finalPayload)) {
                    const rawSym = row.symbol || 'UNKNOWN';
                    const sym = getCanonicalSymbol(rawSym);
                    const exp = row.expiry || 'UNKNOWN';
                    
                    const mappedArray = finalPayload.map((r: any) => ({
                        strike: parseScientificNotation(r.strikePrice || r.strike || r.Strike || r.STRIKE || 0),
                        strikePrice: parseScientificNotation(r.strikePrice || r.strike || r.Strike || r.STRIKE || 0),
                        callPrice: parseScientificNotation(r.callPrice || r.CE_LTP || r.call_ltp || r['CE LTP'] || r['CE Price'] || 0),
                        callOpenInterest: parseScientificNotation(r.callOpenInterest || r.CE_OI || r.call_oi || r['CE OI'] || r['CE Open Interest'] || 0),
                        callVolume: parseScientificNotation(r.callVolume || r.CE_Vol || r.call_volume || r['CE Vol'] || r['CE Volume'] || 0),
                        callChangePercent: parseScientificNotation(r.callChangePercent || r.call_change_p || r.CE_chg_p || r['CE Change %'] || 0),
                        callOpenInterestChange: parseScientificNotation(r.callOpenInterestChange || r.CE_oi_chg || r['CE OI Chg'] || r['CE Change in OI'] || 0),
                        callChange: parseScientificNotation(r.callChange || r.CE_change || r['CE Chg'] || 0),
                        
                        putPrice: parseScientificNotation(r.putPrice || r.PE_LTP || r.put_ltp || r['PE LTP'] || r['PE Price'] || 0),
                        putOpenInterest: parseScientificNotation(r.putOpenInterest || r.PE_OI || r.put_oi || r['PE OI'] || r['PE Open Interest'] || 0),
                        putVolume: parseScientificNotation(r.putVolume || r.PE_Vol || r.put_volume || r['PE Vol'] || r['PE Volume'] || 0),
                        putChangePercent: parseScientificNotation(r.putChangePercent || r.put_change_p || r.PE_chg_p || r['PE Change %'] || 0),
                        putOpenInterestChange: parseScientificNotation(r.putOpenInterestChange || r.PE_oi_chg || r['PE OI Chg'] || r['PE Change in OI'] || 0),
                        putChange: parseScientificNotation(r.putChange || r.PE_change || r['PE Chg'] || 0),
                        
                        callImpliedVolatility: parseScientificNotation(r.callImpliedVolatility || r.CE_IV || r['CE IV'] || 0),
                        putImpliedVolatility: parseScientificNotation(r.putImpliedVolatility || r.PE_IV || r['PE IV'] || 0)
                    }))

                    finalPayload = {
                        optionsData: { [sym]: { [exp]: mappedArray } },
                        indexData: { [sym]: { lastPrice: row.index_ltp || row.indexLtp || 0 } },
                        spotPrice: row.index_ltp || row.indexLtp || 0,
                    };
                }

                let synthesizedTimestamp = row.timestamp_ist || row.timestampIST || finalPayload.timestampIST || finalPayload.timestamp_ist;

                // Synthesize full ISO string from Apps Script's split date and time
                if (!synthesizedTimestamp && row.trade_date && row.timestamp && !row.timestamp.includes('T')) {
                    // e.g. trade_date: '2026-04-25', timestamp: '16:00'
                    const safeTime = row.timestamp.split(':').length === 2 ? `${row.timestamp}:00` : row.timestamp;
                    synthesizedTimestamp = `${row.trade_date}T${safeTime}`;
                }

                if (!synthesizedTimestamp) {
                    synthesizedTimestamp = toIST(row.timestamp || finalPayload.timestamp);
                }

                processed.push({
                    ...finalPayload,
                    ...row,
                    // Ensure per-row spotPrice from D1 overrides any stale blob value
                    spotPrice: row.index_ltp || row.indexLtp || finalPayload.spotPrice || 0,
                    timestampIST: synthesizedTimestamp,
                    data_blob: undefined, // Free memory
                })
            } catch (err) {
                console.error('[Client-Side Decompression] Failed to decode row:', err)
                processed.push(row)
            }
        }
    return processed.flat()
}

// Fetch from Server API (Turso) — supports paginated incremental fetch
export const fetchRangeData = async (
    startDate: string,
    endDate: string,
    symbol: string,
    expiry?: string,
    _isEOD = false,
) => {
    try {
        const auth = getFirebaseAuth()
        const user = auth.currentUser
        const token = user ? await user.getIdToken() : null
        const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

        const allSnapshots: any[] = []
        let offset = 0
        const PAGE_SIZE = 500
        const MAX_ROWS = 2000 // safety cap — a full day is ~375 rows for 1 expiry

        while (offset <= MAX_ROWS) {
            const url = new URL('/api/history', window.location.origin)
            url.searchParams.append('symbol', symbol)
            url.searchParams.append('from', startDate)
            url.searchParams.append('to', endDate)
            if (expiry) url.searchParams.append('expiry', expiry)
            url.searchParams.append('offset', String(offset))
            url.searchParams.append('limit', String(PAGE_SIZE))

            const res = await fetch(url.toString(), { headers })
            if (!res.ok) {
                const errText = await res.text()
                console.error(`[History] API Error ${res.status}:`, errText)
                break
            }

            const contentType = res.headers.get('content-type') || ''
            if (!contentType.includes('application/json')) {
                break
            }

            const dbRows = await res.json()

            if (!Array.isArray(dbRows)) {
                console.error('[History] Turso API Error:', dbRows)
                break
            }

            // console.log( `[History] Turso Page offset=${offset}: ${dbRows.length} rows` )

            if (dbRows.length === 0) break

            const processedRows = await processCompressedHistory(dbRows);
            allSnapshots.push(...processedRows);

            // Check if more pages exist
            const hasMore = res.headers.get('X-Has-More') === 'true'
            if (!hasMore || dbRows.length < PAGE_SIZE) break

            offset += PAGE_SIZE
        }

        // console.log( `[History] Turso Total Rows fetched: ${allSnapshots.length}` )
        return allSnapshots
    } catch (err) {
        console.error('[History] fetchRangeData error:', err)
        return []
    }
}

// ============================================================================
// TIER 5: NiftyTrader API (Final Fallback)
// Pre-calculated OI changes, Greeks, buildup signals
// Used when Worker (Tier 1) fails.
// ============================================================================
export const fetchFromNiftyTrader = async (
    symbol: string,
    expiry: string,
    time?: string, // Optional: for fetching specific minute
): Promise<any> => {
    try {
        // console.log( `[History] Trying NiftyTrader API: ${symbol} ${expiry}${time ? ` @ ${time}` : ''}` )

        const data = await fetchNiftyTraderMinute(symbol as any, expiry, time)

        if (!data) {
            console.warn('[History] NiftyTrader returned no data')
            return null
        }

        // console.log( `[History] NiftyTrader Success: ${data.optionChain?.length || 0} strikes` )
        return data
    } catch (e: any) {
        console.warn('[History] NiftyTrader failed:', e.message)
        return null
    }
}

// ============================================================================
// TIER 4: Upstox Intraday Historical API (Server-processed)
// Fetches 1-min candles with BaseTime OI calculations, crossover counts, etc.
// Used as fallback for Today's data if Turso/Sheet fails.
// ============================================================================
export const fetchFromUpstoxHistorical = async (
    symbol: string,
    expiry?: string,
    baseTime: string = '09:16',
    strikeRange: number = 11,
): Promise<any> => {
    // DEPRECATED: This endpoint (/api/intraday-history) no longer exists.
    // Intraday history is now fetched via tRPC getIntradayHistory procedure.
    return null
}

/**
 * Fetch Intraday Data from Google Sheet CSV (Fallback/Primary for today)
 */
// Renamed for clarity, though exported as original name to avoid breaking imports
export const fetchIntradayFromGoogleSheet = async (symbol: string, expiry?: string): Promise<any[]> => {
    try {
        const now = new Date()
        const istNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }))
        istNow.setHours(0, 0, 0, 0)

        let minTimestamp = istNow.getTime()

        // Weekend Logic: If Sat (6) or Sun (0), allow fetching recent data (e.g. Friday)
        const day = istNow.getDay()
        if (day === 0 || day === 6) {
            minTimestamp = 0
        }

        // DIRECT BROWSER FETCH (Bypasses serverless worker 128MB limit to prevent Cloudflare Error 1102 / 503)
        // @ts-expect-error - symbol safely indexed
        const directUrl = CONFIG.API.SOURCES.INTRADAY_SHEET_LINKS?.[symbol]
        if (!directUrl) {
            console.warn(`[History] No direct Google Sheet link available for ${symbol}`)
            return []
        }

        const directRes = await fetch(directUrl)
        if (!directRes.ok) throw new Error(`Direct Sheet Error: ${directRes.status}`)

        const csvText = await directRes.text()
        const { parseCSV, rowsToSnapshots } = await import('./data-utils')
        let parsedRows = parseCSV(csvText)

        if (!Array.isArray(parsedRows) || parsedRows.length === 0) {
            console.warn(`[History] CSV ZERO lines for ${symbol} (Sheet Empty)`)
            return []
        }

        // Client filtering to match requested parameters
        if (minTimestamp > 0) {
            const minDateIST = new Date(minTimestamp + 5.5 * 3600000)
            const d = minDateIST.getUTCDate().toString().padStart(2, '0')
            const m = (minDateIST.getUTCMonth() + 1).toString().padStart(2, '0')
            const y = minDateIST.getUTCFullYear()
            const dateStr = `${d}-${m}-${y}`

            parsedRows = parsedRows.filter((r: any) => {
                const timeVal = r.timestamp || r.timestampIST || r.time || ''
                const strVal = String(timeVal)
                if (strVal.includes(dateStr)) return true
                const numTs = Number(timeVal)
                if (!isNaN(numTs) && numTs >= minTimestamp) return true
                return false
            })
        }
        if (expiry) {
            parsedRows = parsedRows.filter((r: any) => {
                const ex = r.expirationDate || r.expiry || r.Expiry || ''
                return String(ex) === expiry // exact match
            })
        }

        // Symbol filtering
        parsedRows = parsedRows.filter((r: any) => {
            const sym = r.underlyingName || r.symbol || r.Symbol || r.Underlying || r.instrument_name || ''
            const cleanSym = String(sym)
                .replace(/[^a-zA-Z0-9]/g, '')
                .toUpperCase()
            const reqSym = symbol.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()

            if (reqSym.includes(IndexSymbol.NIFTY) && !reqSym.includes('BANK')) {
                return cleanSym === IndexSymbol.NIFTY || cleanSym === 'NIFTY50' || cleanSym.includes('NIFTY50')
            } else if (reqSym.includes('BANK')) {
                return cleanSym.includes('BANK')
            } else if (reqSym.includes(IndexSymbol.SENSEX)) {
                return cleanSym.includes(IndexSymbol.SENSEX)
            }
            return cleanSym === reqSym
        })

        if (parsedRows.length === 0) return []

        const firstFiltered = parsedRows[0]
        if (firstFiltered && ('strikePrice' in firstFiltered || 'callPrice' in firstFiltered)) {
            return rowsToSnapshots(parsedRows)
        }
        return parsedRows
    } catch (fetchErr: any) {
        console.error(`[History] Direct sheet fetch error: ${fetchErr.message}`)
        return []
    }
}

// GAP FILLING LOGIC
async function fillIntradayGaps(symbol: string, expiry: string, existingData: any[]): Promise<any[]> {
    if (!Array.isArray(existingData)) return []

    // Sort existing data
    const sorted = [...existingData].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
    const timeMap = new Set<string>()

    sorted.forEach((d) => {
        try {
            const ts = d.timestamp
            if (!ts) return
            const date = new Date(ts + 19800000)
            // PERFORMANCE FIX: offset math is much faster than toLocaleTimeString
            const h = date.getUTCHours()
            const m = date.getUTCMinutes()
            const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
            timeMap.add(timeStr)
        } catch (e) { console.warn('Ignored error in history-utils.ts:', e); }
    })

    const missingTimes: string[] = []
    const now = new Date()
    const istNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }))

    // Start from Market Open
    const current = new Date(istNow)
    current.setHours(MARKET_HOURS.OPEN_HOUR, MARKET_HOURS.OPEN_MINUTE, 0, 0)

    // End min(Now, Market Close)
    const close = new Date(istNow)
    close.setHours(MARKET_HOURS.CLOSE_HOUR, MARKET_HOURS.CLOSE_MINUTE, 0, 0)

    // If Now > Close, limit to Close
    const endTime = istNow > close ? close : istNow

    // Safety: Don't fill future gaps if endTime < current (e.g. before market open)
    if (endTime < current) return sorted

    while (current <= endTime) {
        // PERFORMANCE FIX: current is already shifted to IST by parsing logic above
        const h = current.getHours()
        const m = current.getMinutes()
        const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`

        if (!timeMap.has(timeStr)) {
            missingTimes.push(timeStr)
        }
        current.setMinutes(current.getMinutes() + 1)
    }

    // Limits: Fill only small gaps (<= 15 mins) to avoid blocking main thread
    if (missingTimes.length > 0 && missingTimes.length <= 15) {
        // console.log( `[History] Filling ${missingTimes.length} gaps via NiftyTrader Batch: ${missingTimes.join( ', ' )}` )
        // Use expiry from arguments or try to deduce?
        // fetchNiftyTraderBatch requires expiry.
        // It's passed from fetchHistoricalData args.
        if (!expiry) {
            console.warn('[History] Cannot fill gaps without expiry.')
            return sorted
        }

        const batchResults = await fetchNiftyTraderBatch(symbol, expiry, missingTimes)

        batchResults.forEach((data) => {
            if (data) sorted.push(data)
        })

        // Re-sort
        sorted.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
        return sorted
    }

    return sorted
}

// Smart Fetch: TURSO → GOOGLE SHEET → HUGGING FACE FALLBACK CHAIN
export const fetchHistoricalData = async (date: string, symbol: string, expiry?: string) => {
    const today = new Date()
    const targetDate = new Date(date)
    const diffTime = Math.abs(today.getTime() - targetDate.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    const toLocalISO = (d: Date) => {
        const offset = d.getTimezoneOffset() * 60000
        return new Date(d.getTime() - offset).toISOString().split('T')[0]
    }

    const isToday = toLocalISO(today) === date

    try {
        if (isToday || diffDays <= 90) {
            // CACHE IMPLEMENTATION -----------------------------------------
            // CACHE IMPLEMENTATION -----------------------------------------
            // CHANGED: Use IndexedDB (HistoryCache) instead of LocalStorage to avoid 5MB limit
            const CACHE_KEY = `oracle_hist_${symbol}_${date}_${expiry || 'all'}`
            const CACHE_TTL = 60 * 1000 // 1 Minute

            try {
                const { HistoryCache } = await import('./idb-cache')
                const cachedData = await HistoryCache.get(CACHE_KEY)

                if (cachedData) {
                    const now = Date.now()
                    const age = now - cachedData.timestamp

                    // Decision Logic:
                    // 1. If it's a PAST date, and we have data, return it (Permanent Cache)
                    // 2. If it's TODAY, and cache is fresh (< 60s), return it (Throttle)
                    if (!isToday || (isToday && age < CACHE_TTL)) {
                        // console.log(
                        //     `[History] Returning IDB Cached Data for ${symbol} (Age: ${Math.round( age / 1000 )}s)`,
                        // )
                        return cachedData.data
                    }
                }
            } catch (err) {
                console.warn('[History] IDB Cache Read Failed', err)
            }
            // -------------------------------------------------------------

            // console.log( `[History] Fetching from Turso (IsToday: ${isToday})...` )
            let data = await fetchRangeData(date, date, symbol, expiry)

            // Validate Data Integrity
            const now = new Date()
            const istNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }))
            const marketOpen = new Date(istNow)
            marketOpen.setHours(9, 15, 0, 0)
            const marketClose = new Date(istNow)
            marketClose.setHours(15, 30, 0, 0)
            const dataEndTime = istNow > marketClose ? marketClose : istNow

            // Smart Coverage Check (Robust fallback for partial data)
            let isComplete = true
            if (isToday) {
                const expectedMinutes =
                    dataEndTime > marketOpen ? Math.floor((dataEndTime.getTime() - marketOpen.getTime()) / 60000) : 0

                const fetchedCount = data?.length || 0

                // Smart Rule:
                // 1. If Early Morning (< 45 mins from open): Accept any small data (>5 rows)
                // 2. If Later (> 45 mins): Require at least 50% coverage of expected time
                //    (e.g. at 12:00, expected 165 mins. If we have 70 rows (42%), reject -> Fallback to Sheet)

                // On weekends/holidays, skip stringent coverage checks
                // The data in Turso is from the last market day, so coverage will always appear low
                if (!isMarketDay()) {
                    // Non-market day: Turso data is from the LAST trading day.
                    // Check if it covers most of the day (a full market day = ~375 mins).
                    // If Turso backup stopped partway (e.g. 150 rows at 11:43), mark incomplete
                    // so Sheet data can fill the gap.
                    const MIN_FULL_DAY = 300 // ~80% of 375 market minutes
                    if (fetchedCount >= MIN_FULL_DAY) {
                        // console.log( `[History] Non-market day: Turso has ${fetchedCount} rows (sufficient). Accepting.` )
                        isComplete = true
                    } else {
                        // console.log( `[History] Non-market day: Turso has only ${fetchedCount} rows (< ${MIN_FULL_DAY}). Marking incomplete for Sheet merge.` )
                        isComplete = false
                    }
                } else if (expectedMinutes > 45) {
                    const coverageRatio = fetchedCount / expectedMinutes

                    if (fetchedCount === 0) {
                        console.info(
                            `[History] No data in Turso yet (0/${expectedMinutes} mins). Triggering self-healing backfill...`,
                        )
                        isComplete = false
                    } else if (coverageRatio < 0.5) {
                        console.warn(
                            `[History] Low Data Coverage: ${(coverageRatio * 100).toFixed(1)}% (${fetchedCount}/${expectedMinutes} mins). Marking incomplete.`,
                        )
                        isComplete = false
                    }
                } else {
                    // Early morning - Relaxed check
                    isComplete = fetchedCount > 5
                }

                // Attempt Gap Fill (NiftyTrader)
                if (data && data.length > 0 && expiry) {
                    const filled = await fillIntradayGaps(symbol, expiry, data)
                    if (filled.length > data.length) {
                        // console.log( `[History] Gap Fill Success: ${data.length} -> ${filled.length} rows` )
                        data = filled
                        // Re-check completeness? Optionally.
                    }
                }
            }

            // CACHE WRITE --------------------------------------------------
            if (data && data.length > 0) {
                try {
                    // FIX for FILE_ERROR_NO_SPACE: Do NOT cache 'Today's data to IndexedDB.
                    // The live app fetches every 60s, and writing 50MB+ to LevelDB every minute
                    // causes write-amplification and completely fills the browser/disk quota
                    // before Chrome can run garbage collection on the LevelDB store.
                    const shouldCache = !isToday && data.length > 50;

                    if (shouldCache) {
                        const { HistoryCache } = await import('./idb-cache')
                        await HistoryCache.set(CACHE_KEY, data, expiry || '')
                    }
                } catch (e) {
                    console.warn('[History] IDB Write Failed', e)
                }
            }
            // --------------------------------------------------------------

            // ------------------------------------------------------------------
            // SELF-HEALING: Trigger Server-Side Backfill if Turso Data is weak
            // ------------------------------------------------------------------
            if (isToday && !isComplete && isMarketDay()) {
                console.warn(
                    `[History] Data in Turso is weak/incomplete for ${symbol}. Regular backup process should fill this eventually.`,
                )
                // FIXED: Removed aggressive client-side trigger to /api/debug/import
                // this was causing excessive backups when pulse restarted.
            }

            // Fallback for Today: Google Sheet + IDB Cache
            if (isToday && (!isComplete || !data || data.length === 0)) {
                // console.log( '[History] Turso incomplete. Fetching Sheet Data to MERGE with Turso...' )

                // 1. Try IDB Cache first (Fastest)
                const { HistoryCache } = await import('./idb-cache')
                const cachedSheet = await HistoryCache.get(`sheet-${symbol}`)

                let sheetData: any[] | null = null

                if (cachedSheet) {
                    const age = Date.now() - cachedSheet.timestamp
                    if (age < 5 * 60 * 1000) {
                        // 5 min cache for sheet data
                        /*
                        console.log(
                            `[History] Using IDB Cached Sheet Data (${cachedSheet.data.length} rows, Age: ${Math.round( age / 1000 )}s)`,
                        )
                        */
                        sheetData = cachedSheet.data
                    }
                }

                if (!sheetData) {
                    // console.log( '[History] Cache miss/stale. Fetching fresh from Live Sheet...' )
                    sheetData = await fetchIntradayFromGoogleSheet(symbol, expiry)

                    if (sheetData && sheetData.length > 0) {
                        // Update IDB Cache
                        HistoryCache.set(`sheet-${symbol}`, sheetData, expiry || '')
                    }
                }

                // MERGE: Combine Turso (partial) + Sheet data by timestamp dedup
                if (sheetData && sheetData.length > 0) {
                    const tursoData = data || []
                    /*
                    console.log(
                        `[History] Merging: Turso(${tursoData.length}) + Sheet(${sheetData.length}) rows...`,
                    )
                    */

                    // Build a timestamp → snapshot map, Turso first then Sheet fills gaps
                    const mergedMap = new Map<string, any>()

                    // Helper: extract a timestamp key for dedup (minute resolution)
                    const getTimeKey = (snap: any): string => {
                        const ts = snap.timestamp || snap.timestampIST || 0
                        if (typeof ts === 'number' && ts > 946684800000) {
                            // Normalize to minute resolution
                            const d = new Date(ts)
                            const offset = 5.5 * 60 * 60 * 1000
                            const ist = new Date(d.getTime() + offset)
                            return ist.toISOString().substring(0, 16) // YYYY-MM-DDTHH:MM
                        }
                        if (typeof ts === 'string') {
                            return ts.substring(0, 16)
                        }
                        return String(ts)
                    }

                    // First pass: Turso data (higher priority — has pre-calculated metrics)
                    for (const snap of tursoData) {
                        const key = getTimeKey(snap)
                        if (key && key !== '0' && key !== 'undefined') {
                            mergedMap.set(key, snap)
                        }
                    }

                    // Second pass: Sheet data fills gaps (only add if key doesn't exist)
                    for (const snap of sheetData) {
                        const key = getTimeKey(snap)
                        if (key && key !== '0' && key !== 'undefined' && !mergedMap.has(key)) {
                            mergedMap.set(key, snap)
                        }
                    }

                    const merged = Array.from(mergedMap.values()).sort(
                        (a, b) => (a.timestamp || 0) - (b.timestamp || 0),
                    )

                    /*
                    console.log(
                        `[History] Merged Result: ${merged.length} unique rows (Turso: ${tursoData.length}, Sheet: ${sheetData.length})`,
                    )
                    */

                    // Cache the merged result
                    try {
                        if (!isToday) {
                            await HistoryCache.set(CACHE_KEY, merged, expiry || '')
                        }
                    } catch (e) {
                        console.warn('[History] IDB Write Failed for merged data', e)
                    }

                    return merged
                }
            }

            if (isToday && isComplete) {
                return data
            }

            // SAFETY NET: If Sheet failed but we had partial Turso data, use it!
            if (data && data.length > 0) {
                console.warn(
                    `[History] Sheet Fallback failed/empty. Returning partial Turso data (${data.length} rows) as last resort.`,
                )
                return data
            }
        } else {
            // For Historical Data (Not Today), if Turso is empty/low, fall back to HF
            // console.log( '[History] Turso empty/incomplete for Past Date. Falling back to HF...' )
        }

        // ------------------------------------------------------------------
        // FINAL FALLBACK: Hugging Face (For Past Dates) or Sheet (For Today)
        // ------------------------------------------------------------------

        // Fallback: Hugging Face (Only if NOT today, or if today failed completely)
        if (!isToday || (isToday && diffDays > 0)) {
            // console.log( `[History] Trying Hugging Face Proxy for ${date}...` )

            const auth = getFirebaseAuth()
            const token = auth.currentUser ? await auth.currentUser.getIdToken() : null
            const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

            // Attempt 1: Fetch JSON Archive
            try {
                const jsonUrl = `/api/history-proxy?symbol=${symbol}&date=${date}&type=json`
                const res = await fetch(jsonUrl, { headers })

                if (res.ok) {
                    const contentType = res.headers.get('content-type') || ''
                    if (contentType.includes('application/json')) {
                        const rows = await res.json()

                        if (rows._status === 'not_found') {
                            // console.log( `[History] HF JSON not found for ${date}. Trying CSV...` )
                        } else if (Array.isArray(rows) && rows.length > 0) {
                            // console.log( `[History] Loaded from HF Proxy (JSON): ${rows.length} records` )
                            const snapshots: any[] = []

                            for (const row of rows) {
                                if (row.dataBlob) {
                                    try {
                                        const compressed = fromBase64(row.dataBlob)
                                        const snapshot = await decompressBlob(compressed)
                                        if (!snapshot.timestamp) snapshot.timestamp = row.timestamp
                                        snapshots.push(snapshot)
                                    } catch (err) {
                                        console.warn('Failed to decompress archive row')
                                    }
                                }
                            }
                            // If rows are already expanded (no dataBlob), just return them
                            if (snapshots.length === 0 && rows.length > 0 && !rows[0].dataBlob) {
                                return rows
                            }
                            if (snapshots.length > 0) return snapshots
                        }
                    }
                }
            } catch (e) {
                console.warn('[History] HF Proxy JSON fetch failed', e)
            }

            // Attempt 2: Fetch CSV (Legacy)
            try {
                const csvUrl = `/api/history-proxy?symbol=${symbol}&date=${date}&type=csv`
                const res = await fetch(csvUrl, { headers })

                if (res.ok) {
                    const contentType = res.headers.get('content-type')

                    if (contentType && contentType.includes('application/json')) {
                        const jsonRes = await res.json()
                        if (jsonRes._status === 'not_found') {
                            // console.log( `[History] HF CSV not found for ${date}.` )
                        }
                    } else {
                        const blob = await res.arrayBuffer()

                        try {
                            // This is plain CSV.ZST? No, likely just CSV in this context or handled by proxy
                            const csvText = new TextDecoder().decode(blob)
                            const { parseCSV, rowsToSnapshots } = await import('./data-utils')
                            const rows = parseCSV(csvText)
                            // console.log( `[History] Loaded from HF Proxy (CSV): ${rows.length} records` )
                            return rowsToSnapshots(rows)
                        } catch (gzipErr) {
                            console.warn('[History] CSV Processing failed')
                        }
                    }
                }
            } catch (e) {
                // Intentionally silent
            }
        }

        // console.log( '[History] HF Data unavailable.' )

        // Final Safety Net: Try Sheet one last time ONLY IF TODAY OR LAST TRADING DAY
        // This is critical for weekends where "Historic Day" is Friday but Turso might be empty/stale
        const { getLastTradingDay, formatDateToYYYYMMDD } = await import('./date-utils')
        const lastTradingDay = formatDateToYYYYMMDD(getLastTradingDay())
        const isLastTradingDay = lastTradingDay === date

        if (isToday || isLastTradingDay) {
            // console.log( `[History] Final fallback to Google Sheet (Today/LastTradingDay: ${lastTradingDay})...` )
            const finalSheet = await fetchIntradayFromGoogleSheet(symbol, expiry)
            if (finalSheet && finalSheet.length > 0) return finalSheet
        }

        return []
    } catch (e) {
        console.error('History Fetch Failed (Turso & HF)', e)
        // Last Ditch: Only fallback if today
        if (new Date().toISOString().split('T')[0] === date) return fetchIntradayFromGoogleSheet(symbol, expiry)
        return []
    }
}

export async function fetchLotSizeFromCSV(symbol: string): Promise<number> {
    try {
        const resp = await fetch(API_CONFIG.SOURCES.EXPIRY_CSV);
        if (!resp.ok) return 0;
        const text = await resp.text();
        const lines = text.split('\n');
        
        let canonicalSym = (getCanonicalSymbol(symbol) || symbol) as string;
        
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i]?.trim();
            if (!line) continue;
            const cols = line.split(',');
            if (cols.length >= 3) {
                if (cols[0] === canonicalSym) {
                    return parseInt(cols[2] || '0', 10);
                }
            }
        }
    } catch (e) { console.warn('Ignored error in history-utils.ts:', e); }
    return 0;
}

